const INTERACTIVE_DRAG_TARGET_SELECTOR = [
  "button",
  "input",
  "textarea",
  "select",
  "option",
  "a",
  "[role='button']",
  "[data-no-drag]",
].join(", ")

type PointerStartLike = {
  button?: number
  currentTarget: EventTarget | null
  pointerId?: number
  preventDefault: () => void
  target: EventTarget | null
}

type PointerSessionOptions = {
  captureTarget?: EventTarget | null
  onEnd?: (reason: "cancel" | "end") => void
  onMove: (event: PointerEvent) => void
}

export function shouldSkipPointerDragStart(event: PointerStartLike) {
  if (!(event.target instanceof Element) || !(event.currentTarget instanceof Element)) {
    return false
  }

  const interactiveTarget = event.target.closest(INTERACTIVE_DRAG_TARGET_SELECTOR)
  return Boolean(interactiveTarget && event.currentTarget.contains(interactiveTarget))
}

export function startPointerSession(
  event: PointerStartLike,
  { captureTarget = event.currentTarget, onEnd, onMove }: PointerSessionOptions
) {
  if (typeof event.button === "number" && event.button !== 0) {
    return () => {}
  }

  event.preventDefault()

  const pointerId = typeof event.pointerId === "number" ? event.pointerId : null
  const pointerTarget = captureTarget instanceof Element ? captureTarget : null
  let active = true

  const cleanup = (reason: "cancel" | "end") => {
    if (!active) {
      return
    }

    active = false
    window.removeEventListener("pointermove", handleMove)
    window.removeEventListener("pointerup", handleUp)
    window.removeEventListener("pointercancel", handleCancel)
    window.removeEventListener("blur", handleBlur)
    pointerTarget?.removeEventListener("lostpointercapture", handleLostPointerCapture)

    if (pointerTarget && pointerId !== null) {
      try {
        if (pointerTarget.hasPointerCapture(pointerId)) {
          pointerTarget.releasePointerCapture(pointerId)
        }
      } catch {}
    }

    onEnd?.(reason)
  }

  const isSamePointer = (nextEvent: PointerEvent) =>
    pointerId === null || nextEvent.pointerId === pointerId

  const handleMove = (nextEvent: PointerEvent) => {
    if (!active || !isSamePointer(nextEvent)) {
      return
    }

    onMove(nextEvent)
  }

  const handleUp = (nextEvent: PointerEvent) => {
    if (!isSamePointer(nextEvent)) {
      return
    }

    cleanup("end")
  }

  const handleCancel = (nextEvent: PointerEvent) => {
    if (!isSamePointer(nextEvent)) {
      return
    }

    cleanup("cancel")
  }

  const handleBlur = () => {
    cleanup("cancel")
  }

  const handleLostPointerCapture = () => {
    cleanup("cancel")
  }

  window.addEventListener("pointermove", handleMove)
  window.addEventListener("pointerup", handleUp)
  window.addEventListener("pointercancel", handleCancel)
  window.addEventListener("blur", handleBlur)
  pointerTarget?.addEventListener("lostpointercapture", handleLostPointerCapture)

  if (pointerTarget && pointerId !== null) {
    try {
      pointerTarget.setPointerCapture(pointerId)
    } catch {}
  }

  return () => cleanup("cancel")
}
